/* línkè · 文字颜色中枢（2026-07-11，仿 bg.js/accent.js） =============
   三个槽位对应全站字色的三级变量（tokens.css 里每主题各有一套原生值）：
     main  → --text        主文字
     dim   → --text-dim    副文字
     faint → --text-faint  提示文字
   每槽独立可调：null=跟随主题（摘掉内联，主题原色复活）；
     '@id'  = 预设「色调身份」→ 明度按【当前主题×槽位】现算（深主题字浅、
              浅主题字深，切主题永远可读）；
     '#hex' = 自定义具体色（字面保留，不跨主题变形——你为当前主题亲手挑的）。
   存 localStorage（JSON 一条），每台设备各自记，同主题/强调色待遇不进云同步。
   theme.js 的 applyTheme 切主题时会调 applyStoredTextColors() 重算预设。
   ================================================================ */
import { useState, useEffect } from 'react';

export const STORAGE_TEXT = 'lingke.textColor';

/* 槽位定义：key=存储键，cssVar=接管的变量，name/desc 给侧边栏 UI 用。 */
export const TEXT_SLOTS = [
  { key: 'main', cssVar: '--text', name: '主文字' },
  { key: 'dim', cssVar: '--text-dim', name: '副文字' },
  { key: 'faint', cssVar: '--text-faint', name: '提示文字' },
];

/* 预设「色调身份」：定色相+饱和，明度交给主题×槽位。挑的都是长文可读的
   低刺激色系；饱和度整体压得比背景预设低（字色太艳伤眼）。 */
export const TEXT_PRESETS = [
  { id: 'ink', name: '墨', hue: 260, sat: 0.14 },
  { id: 'warm', name: '暖棕', hue: 30, sat: 0.38 },
  { id: 'rouge', name: '胭脂', hue: 340, sat: 0.42 },
  { id: 'sage', name: '青竹', hue: 155, sat: 0.28 },
  { id: 'mist', name: '雾蓝', hue: 220, sat: 0.36 },
  { id: 'lav', name: '薰衣草', hue: 270, sat: 0.36 },
];

/* 每主题的「槽位明度阶梯 + 饱和柔化系数 k」。明度取自 tokens.css 各主题
   原生 --text / --text-dim / --text-faint 的实测 L → 预设与原生一样深浅。 */
const THEME_TEXT = {
  dark: { main: 0.93, dim: 0.7, faint: 0.46, k: 0.85 },
  light: { main: 0.16, dim: 0.37, faint: 0.54, k: 1.0 },
  rose: { main: 0.14, dim: 0.38, faint: 0.59, k: 1.0 },
  snow: { main: 0.17, dim: 0.37, faint: 0.59, k: 1.0 },
};

const _listeners = new Set();

function _validHex(v) {
  return typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v);
}
function _validToken(v) {
  return (
    _validHex(v) ||
    (typeof v === 'string' && v[0] === '@' && TEXT_PRESETS.some((p) => p.id === v.slice(1)))
  );
}

function rgbToHex(r, g, b) {
  const h = (n) => Math.min(255, Math.max(0, Math.round(n))).toString(16).padStart(2, '0');
  return '#' + h(r) + h(g) + h(b);
}
/* HSL(0-360,0-1,0-1) → #hex（与 bg.js 同款实现） */
function hslToHex(h, s, l) {
  h = (((h % 360) + 360) % 360) / 360;
  const f = (n) => {
    const k = (n + h * 12) % 12;
    const a = s * Math.min(l, 1 - l);
    return l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  };
  return rgbToHex(f(0) * 255, f(8) * 255, f(4) * 255);
}

/* 读 <html data-theme>（真正在渲染的主题），不读 localStorage：切主题时 applyTheme 先改
   data-theme、最后才存 localStorage，读 localStorage 会拿到【旧主题】→ 深色切绯霞时
   预设字色还按深色算成浅字（2026-10-08 铃逮到）。 */
function currentTheme() {
  try {
    const v = document.documentElement.getAttribute('data-theme');
    if (v === 'light' || v === 'rose' || v === 'snow') return v;
  } catch {}
  return 'dark';
}

/* 预设在【指定主题×槽位】下的具体字色 hex（UI 画色块 + 应用时都用它）。 */
export function presetColorFor(presetId, slotKey, themeId) {
  const p = TEXT_PRESETS.find((x) => x.id === presetId);
  const th = THEME_TEXT[themeId] || THEME_TEXT.dark;
  if (!p || th[slotKey] == null) return null;
  return hslToHex(p.hue, p.sat * th.k, th[slotKey]);
}

/* 槽位 token → 当前主题下的具体字色 hex；null/非法 → null（跟随主题）。 */
function resolveSlot(token, slotKey) {
  if (_validHex(token)) return token;
  if (typeof token === 'string' && token[0] === '@') {
    return presetColorFor(token.slice(1), slotKey, currentTheme());
  }
  return null;
}

/* 当前主题下该槽的原生字色（自定义取色器的默认起点）。读的是 tokens.css 计算值。 */
export function themeSlotColor(slotKey) {
  const slot = TEXT_SLOTS.find((s) => s.key === slotKey);
  if (!slot) return '#ece6f4';
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(slot.cssVar).trim();
    if (_validHex(v)) return v;
  } catch {}
  return '#ece6f4';
}

/* 返回存的三槽 token：{main, dim, faint}，每项 '@id' | '#hex' | null。 */
export function getTextColors() {
  const out = { main: null, dim: null, faint: null };
  try {
    const raw = localStorage.getItem(STORAGE_TEXT);
    if (raw) {
      const d = JSON.parse(raw);
      for (const s of TEXT_SLOTS) {
        if (_validToken(d?.[s.key])) out[s.key] = d[s.key];
      }
    }
  } catch {}
  return out;
}

export function applyTextColors(state) {
  const root = document.documentElement;
  for (const s of TEXT_SLOTS) {
    const hex = resolveSlot(state?.[s.key], s.key);
    if (hex) root.style.setProperty(s.cssVar, hex);
    else root.style.removeProperty(s.cssVar);
  }
}

function _save(state) {
  try {
    if (state.main || state.dim || state.faint) {
      localStorage.setItem(STORAGE_TEXT, JSON.stringify(state));
    } else {
      localStorage.removeItem(STORAGE_TEXT);
    }
  } catch {}
  applyTextColors(state);
  _listeners.forEach((fn) => {
    try {
      fn(state);
    } catch {}
  });
}

/* 改一个槽（token='@id'|'#hex'|null），其余槽不动。 */
export function setTextColor(slotKey, token) {
  const state = getTextColors();
  state[slotKey] = _validToken(token) ? token : null;
  _save(state);
}

/* 一键全部还原 = 三槽都回「跟随主题」。 */
export function resetTextColors() {
  _save({ main: null, dim: null, faint: null });
}

/* 启动时（main.jsx，首帧前）恢复上次选色，避免刷新闪回主题原字色。
   theme.js 切主题时也调它重算 @ 预设。 */
export function applyStoredTextColors() {
  applyTextColors(getTextColors());
}

/* React hook：[{main,dim,faint}, setTextColor, resetTextColors]。 */
export function useTextColors() {
  const [state, set] = useState(() => getTextColors());
  useEffect(() => {
    _listeners.add(set);
    return () => _listeners.delete(set);
  }, []);
  return [state, setTextColor, resetTextColors];
}
